import React, { useState } from 'react';
import {
  Checkbox,
  Table,
  TableProps,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmpty, isNil } from 'ramda';
import { Box, SxProps } from '@mui/system';

import { OrderDirection } from 'src/enums/OrderDirection';
import { Meta } from 'src/types/meta';
import { DataTableColumn } from 'src/types/dataTable';
import { PAGINATION_ROWS_PER_PAGE } from 'src/utils/constants';
import { decamelize } from 'src/utils/keysConverter';
import clsx from 'src/utils/clsx';
import Loader from 'src/components/Loader';
import Pagination from 'src/components/Pagination';

import styles from './styles';

export type DataTableProps<T> = {
  meta?: Meta;
  loading: boolean;
  columns: DataTableColumn<T>[];
  page?: number;
  resources: T[];
  defaultOrderBy?: string;
  defaultOrderDirection?: OrderDirection;
  noResourcesText?: string;
  onPageChange?: (page: number) => void;
  onRequestSort?: (orderBy: string, orderDirection?: OrderDirection) => void;
  renderRowSubComponent?: (resource: T, inside?: boolean) => JSX.Element;
  tableFooterElement?: React.ReactNode;
  subComponentInsideRow?: boolean;
  tableSx?: TableProps['sx'];
  sx?: SxProps<Theme>;
  expanded?: Record<number, boolean>;
  onExpand?: (rowId: number) => void;
  size?: 'medium' | 'small';
  rowPerPage?: number;
  selected?: number[];
  isCheckboxSelection?: boolean;
  isDisableSelectAllCheckbox?: boolean;
  isCheckboxVisible?: (resource: T) => boolean;
  onSelectedChange?: (ids: ID[]) => void;
  headerTooltips?: Record<string, string>;
  onRowClick?: (resource: T) => void;
  getRowSx?: (resource: T) => SxProps<Theme>;
};

function DataTable<T extends { id: ID }>(props: Readonly<DataTableProps<T>>): JSX.Element {
  const {
    meta,
    columns,
    loading,
    defaultOrderBy = 'id',
    defaultOrderDirection = OrderDirection.asc,
    onRequestSort,
    onPageChange,
    resources: propsResources,
    noResourcesText = 'No content to display',
    renderRowSubComponent,
    subComponentInsideRow = false,
    sx,
    tableSx,
    expanded = {},
    onExpand,
    page = 1,
    size = 'medium',
    rowPerPage = PAGINATION_ROWS_PER_PAGE,
    selected = [],
    isCheckboxSelection = false,
    isDisableSelectAllCheckbox = false,
    isCheckboxVisible,
    onSelectedChange,
    headerTooltips,
    tableFooterElement,
    onRowClick,
    getRowSx,
  } = props;
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(defaultOrderDirection);

  const resources = propsResources || [];
  const allowedColumns = columns.filter(column => column.isAllow ?? true);
  const colSpan = isCheckboxSelection ? allowedColumns.length + 1 : allowedColumns.length;

  const handleRequestSort = (name: string, sortingConfig: DataTableProps<T>['columns'][number]['sortingConfig']) => {
    const isSameOrderBy = orderBy === name;
    const isOrderDirectionAsc = orderDirection === OrderDirection.asc;
    const newOrderDirection = isSameOrderBy && isOrderDirectionAsc ? OrderDirection.desc : OrderDirection.asc;
    const isNewOrderDirectionAsc = newOrderDirection === OrderDirection.asc;

    const secondarySortingColumn = sortingConfig?.secondaryOrderingColumn;
    const isSecondarySortingEnabled = Boolean(secondarySortingColumn);

    const decamelizedName = decamelize(isSecondarySortingEnabled ? `${name},${secondarySortingColumn}` : name);

    setOrderBy(name);
    setOrderDirection(newOrderDirection);

    const usePositiveSortingValue = sortingConfig?.inverseOrdering ? !isNewOrderDirectionAsc : isNewOrderDirectionAsc;
    const newSortingValue = usePositiveSortingValue ? decamelizedName : `-${decamelizedName}`;

    onRequestSort(newSortingValue, newOrderDirection);
    if (isCheckboxSelection) {
      onSelectedChange([]);
    }
  };

  const handleExpand = (id: number) => () => {
    onExpand(id);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, pageIndex: number) => {
    onPageChange(pageIndex + 1);
    if (isCheckboxSelection) {
      onSelectedChange([]);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resources.filter(resource => isCheckboxVisible?.(resource)).map(resource => resource.id);
      onSelectedChange(newSelecteds);
      return;
    }
    onSelectedChange([]);
  };

  const handleSelectOneClick = (id: number) => () => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    onSelectedChange(newSelected);
  };

  const isSelected = (name: number) => selected.indexOf(name) !== -1;

  const renderLoaderCells = () => (
    <TableRow sx={styles.rowDefaultBackground}>
      <TableCell size={size} variant="head" align="center" colSpan={colSpan}>
        <Loader />
      </TableCell>
    </TableRow>
  );

  const renderHeadCell = (column: DataTableColumn<T>) => {
    const { width, align = 'left', name, sortable, head, isAllow = true, headCellSx = {}, sortingConfig } = column;
    if (!isAllow) return null;

    const tooltipData = headerTooltips?.[head];

    const tableContent = sortable ? (
      <TableSortLabel
        active={orderBy === name}
        direction={orderBy === name ? orderDirection : OrderDirection.asc}
        onClick={() => handleRequestSort(name, sortingConfig)}
      >
        {head}
      </TableSortLabel>
    ) : (
      <Box>{head}</Box>
    );

    return (
      <TableCell width={width} key={name} variant="head" align={align} sx={headCellSx}>
        {tooltipData ? (
          <Tooltip title={tooltipData} variant="light" arrow>
            {tableContent}
          </Tooltip>
        ) : (
          tableContent
        )}
      </TableCell>
    );
  };

  const renderBodyCell = (column: DataTableColumn<T>, resource: T, isSubComponentInsideRow = false) => {
    const { name, cellAlign, colSpan: columnColSpan, sx: columnSx, renderCell } = column;
    const cellStyles = clsx(columnSx, [[styles.contentCell, isSubComponentInsideRow]]);

    return (
      <TableCell align={cellAlign} size={size} key={name} sx={cellStyles} colSpan={columnColSpan}>
        {renderCell(resource)}
      </TableCell>
    );
  };

  const renderEmptyResultCells = (): JSX.Element => {
    return (
      <TableRow sx={styles.rowDefaultBackground}>
        <TableCell align="center" colSpan={colSpan}>
          <Typography component="p" variant="body1" sx={styles.emptyCellText}>
            {noResourcesText}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderHeadCells = (): JSX.Element => {
    const isCheckedSelectAllCheckbox =
      resources.length > 0 &&
      !isDisableSelectAllCheckbox &&
      selected.length === resources.filter(resource => isCheckboxVisible?.(resource)).length;

    return (
      <>
        {isCheckboxSelection && (
          <TableCell sx={styles.checkbox}>
            <Checkbox
              color="primary"
              checked={isCheckedSelectAllCheckbox}
              disabled={isDisableSelectAllCheckbox}
              onChange={handleSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          </TableCell>
        )}
        {allowedColumns.map(column => renderHeadCell(column))}
      </>
    );
  };

  const renderBodyCells = (): JSX.Element | JSX.Element[] => {
    if (isEmpty(resources)) {
      return renderEmptyResultCells();
    }

    if (subComponentInsideRow) {
      return resources.map((resource: T, index) => {
        const isItemSelected = isSelected(Number(resource.id));
        const labelId = `enhanced-table-checkbox-${index}`;

        return (
          <TableRow key={resource.id} sx={styles.wrapperRow}>
            <TableCell size={size} colSpan={colSpan} sx={styles.wrapperCell}>
              <Table sx={styles.tableWrapper}>
                <TableBody>
                  <TableRow>
                    {isCheckboxSelection && (
                      <TableCell sx={styles.checkbox} size={size}>
                        {isCheckboxVisible?.(resource) ? (
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={handleSelectOneClick(Number(resource.id))}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    {allowedColumns.map(column => renderBodyCell(column, resource, true))}
                  </TableRow>
                  <TableRow>
                    <TableCell size={size} colSpan={colSpan} sx={styles.subrowContentCell}>
                      {renderRowSubComponent ? renderRowSubComponent(resource) : null}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        );
      });
    }

    return resources
      ? resources.map((resource: T) => {
          const isExpandedRow = expanded[resource.id];
          const resourceCells = allowedColumns.map(column => renderBodyCell(column, resource));

          const RowSubComponent = renderRowSubComponent ? renderRowSubComponent(resource) : null;

          return (
            <React.Fragment key={resource.id}>
              {onExpand && !isNil(RowSubComponent) ? (
                <TableRow
                  key={resource.id}
                  sx={styles.expandedRow}
                  data-row-expanded={isExpandedRow}
                  onClick={handleExpand(Number(resource.id))}
                >
                  {resourceCells}
                </TableRow>
              ) : (
                <TableRow
                  key={resource.id}
                  onClick={onRowClick ? () => onRowClick(resource) : undefined}
                  sx={[onRowClick ? { cursor: 'pointer' } : {}, getRowSx?.(resource) ?? {}] as SxProps<Theme>}
                >
                  {resourceCells}
                </TableRow>
              )}

              {RowSubComponent}
            </React.Fragment>
          );
        })
      : null;
  };

  return (
    <TableContainer sx={sx}>
      <Table sx={[...(Array.isArray(tableSx) ? tableSx : [tableSx])]}>
        <TableHead>
          <TableRow>{renderHeadCells()}</TableRow>
        </TableHead>
        <TableBody>{loading ? renderLoaderCells() : renderBodyCells()}</TableBody>
        <TableFooter>{!tableFooterElement || loading || isEmpty(resources) ? null : tableFooterElement}</TableFooter>
      </Table>

      {meta?.count > rowPerPage && (
        <Pagination
          sx={styles.pagination}
          count={meta?.count}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowPerPage}
        />
      )}
    </TableContainer>
  );
}

export default DataTable;
